import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/contexts/ThemeContext";
import { useUserLocation } from "@/hooks/useUserLocation";
import { LocationChooser } from "@/components/scenarios/LocationChooser";
import ScenarioCardNearYou from "@/components/scenarios/ScenarioCardNearYou";
import {
  Search, MapPin, Building2, ArrowRight, TrendingUp, AlertTriangle,
  FileText, Loader2, ChevronDown, Activity, DollarSign, ExternalLink,
  Heart, Baby, Ambulance, Clock, ArrowUpDown, Filter,
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { trackSearch } from "@/utils/analytics";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import { isNativeApp } from "@/lib/platform";
import { loadStaticData, loadTranslatedStaticData } from "@/lib/offlineData";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import FlagBackground, { tunerTap } from "@/components/FlagBackground";
import { scenarioStorage } from "@/lib/scenarioStorage";
import { HospitalScenarioDeals } from "@/components/HospitalScenarioDeals";
import { getHospitalGrade } from "@/lib/grades";
import { titleCaseHospital, titleCaseCity } from "@/lib/hospitalName";

/* Patriotic design tokens — light / dark */
const T_LIGHT = {
  bg: "var(--civic-cream)",
  bgDark: "var(--civic-navy)",
  text: "var(--civic-ink)",
  textMuted: "rgba(var(--civic-ink-rgb),0.62)",
  green: "var(--civic-money)",
  gold: "var(--civic-gold)",
  red: "var(--civic-red)",
  blue: "var(--civic-navy)",
  border: "rgba(var(--civic-ink-rgb),0.12)",
  hoverBg: "rgba(var(--civic-red-rgb),0.04)",
  buttonBorder: "rgba(var(--civic-ink-rgb),0.40)",
  shadow: "rgba(0,0,0,0.08)",
  cardBg: "#ffffff",
  inputBg: "#ffffff",
};
const T_DARK = {
  bg: "var(--civic-bg-dark)",
  bgDark: "#050e1a",
  text: "var(--civic-cream)",
  textMuted: "rgba(var(--civic-cream-rgb),0.55)",
  green: "#4ade80",
  gold: "var(--civic-gold)",
  red: "var(--civic-red)",
  blue: "#5B9BD5",
  border: "rgba(var(--civic-cream-rgb),0.12)",
  hoverBg: "rgba(var(--civic-red-rgb),0.10)",
  buttonBorder: "rgba(var(--civic-cream-rgb),0.35)",
  shadow: "rgba(0,0,0,0.35)",
  cardBg: "var(--civic-navy-deeper)",
  inputBg: "var(--civic-navy-deeper)",
};
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const ICON_MAP: Record<string, any> = { Heart, Baby, Ambulance, Activity };

// Labels are i18n keys — resolve with t(cat.labelKey) at render time so they
// follow the selected language.
const SEARCH_CATEGORIES = [
  { value: "scenario", labelKey: "search:categories.scenario" },
  { value: "hospital", labelKey: "search:categories.hospital" },
  { value: "procedure", labelKey: "search:categories.procedure" },
  { value: "cpt", labelKey: "search:categories.cpt" },
  { value: "hcpcs", labelKey: "search:categories.hcpcs" },
  { value: "rev", labelKey: "search:categories.rev" },
  { value: "ms-drg", labelKey: "search:categories.msDrg" },
] as const;

// Curated consumer order for the default "Featured" scenario list — the everyday
// conditions people actually pre-research, base scenario of each family first.
// (Kept in sync with the same list on the Scenarios page.)
const CONSUMER_PRIORITY = [
  "Uncomplicated Pregnancy & Vaginal Delivery", "C-Section Delivery",
  "Broken Arm Treatment", "Broken Wrist Repair", "Broken Ankle Repair", "Broken Hip Repair",
  "Ankle Sprain Treatment", "Concussion Evaluation", "Chest Pain Workup", "Heart Attack Treatment",
  "Appendectomy (Emergency", "Gallbladder Removal (Laparoscopic", "Hernia Repair (Inguinal",
  "Knee Arthroscopy", "Kidney Stone Prevention", "Urinary Tract Infection (UTI)",
  "Colonoscopy (Screening", "Mammogram (Screening", "Cataract Surgery",
  "Wisdom Teeth Extraction", "Tonsillectomy (Adult", "Strep Throat Treatment",
  "Community-Acquired Pneumonia — Outpatient", "Childhood Asthma", "COVID-19 — Outpatient",
  "Food Poisoning Treatment", "Gallstone Attack", "Vasectomy (Male Sterilization", "Annual Wellness Visit",
];
const consumerPrio = (name: string) => {
  const lower = (name || "").toLowerCase();
  const i = CONSUMER_PRIORITY.findIndex(k => lower.includes(k.toLowerCase()));
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

type SearchCategory = typeof SEARCH_CATEGORIES[number]["value"];

// The four code-lookup categories (folded into the "Medical code" dropdown).
const CODE_CATEGORIES = ["cpt", "hcpcs", "rev", "ms-drg"] as const;

// Clickable starter codes shown when a code category is selected with no
// query yet — all verified present in the pricing tables.
const CODE_EXAMPLES: Record<string, { code: string; label: string }[]> = {
  cpt: [
    { code: "99213", label: "Office visit" },
    { code: "45378", label: "Colonoscopy" },
    { code: "70450", label: "CT scan, head" },
  ],
  hcpcs: [
    { code: "J1885", label: "Ketorolac injection" },
    { code: "A0428", label: "Ambulance (BLS)" },
    { code: "G0121", label: "Screening colonoscopy" },
  ],
  rev: [
    { code: "0450", label: "Emergency room" },
    { code: "0250", label: "Pharmacy" },
    { code: "0360", label: "Operating room" },
  ],
  "ms-drg": [
    { code: "470", label: "Hip / knee replacement" },
    { code: "871", label: "Sepsis" },
    { code: "807", label: "Vaginal delivery" },
  ],
};

// Config map for code-type searches — accounts for column naming differences
const CODE_TYPE_CONFIG: Record<string, {
  table: string;
  codeCol: string;
  descCol: string;
  avgChargeCol: string;
  minChargeCol: string;
  maxChargeCol: string;
  medianChargeCol: string;
  route: string;
  hasSettingType: boolean;
  codePrefix?: string; // prefix stored in DB (e.g. "MS-" for DRG codes)
}> = {
  cpt: {
    table: "cpt_pricing_averages",
    codeCol: "cpt_code",
    descCol: "procedure_description",
    avgChargeCol: "avg_gross_charge",
    minChargeCol: "min_gross_charge",
    maxChargeCol: "max_gross_charge",
    medianChargeCol: "median_gross_charge",
    route: "/cpt",
    hasSettingType: true,
  },
  hcpcs: {
    table: "hcpcs_pricing_averages",
    codeCol: "hcpcs_code",
    descCol: "procedure_description",
    avgChargeCol: "avg_gross_charge",
    minChargeCol: "min_gross_charge",
    maxChargeCol: "max_gross_charge",
    medianChargeCol: "median_gross_charge",
    route: "/hcpcs",
    hasSettingType: true,
  },
  rev: {
    table: "revenue_pricing_averages",
    codeCol: "revenue_code",
    descCol: "revenue_description",
    avgChargeCol: "avg_charge",
    minChargeCol: "min_charge",
    maxChargeCol: "max_charge",
    medianChargeCol: "median_charge",
    route: "/revenue",
    hasSettingType: true,
  },
  "ms-drg": {
    table: "drg_pricing_averages",
    codeCol: "drg_code",
    descCol: "procedure_description",
    avgChargeCol: "avg_gross_charge",
    minChargeCol: "min_gross_charge",
    maxChargeCol: "max_gross_charge",
    medianChargeCol: "median_gross_charge",
    route: "/drg",
    hasSettingType: false,
    codePrefix: "MS-",
  },
};

interface HospitalResult {
  id: string;
  name: string;
  city: string;
  state: string;
  avg_gross_markup_percentage: number | null;
  // Median-baseline markup — the metric shame_rank is ordered by. Rows sort
  // and display this so rank pills stay monotonic with the list order.
  median_gross_markup_percentage?: number | null;
  total_procedures: number | null;
  outlier_expensive_count: number | null;
  shame_rank?: number;
  hospital_type?: string | null;
  scenario_count?: number;
}

interface ScenarioResult {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  color: string;
  duration: string;
  total_procedures: number;
  phases: string[];
  estimated_cost: number;
  real_total_cost: number | null;
  severity_level?: number | null;
  scenario_family_id?: string | null;
  variant_count?: number;
  phaseBreakdown?: Record<string, number>;
  tags?: string[];
  created_by?: string | null;
  is_verified?: boolean;
}

interface ProcedureResult {
  code: string;
  code_type: string;
  name: string;
  consumer_friendly_description: string | null;
  llm_quick_name: string | null;
  avg_gross_charge: number | null;
  total_hospitals: number | null;
}

interface CodeResult {
  code: string;
  description: string;
  avg_charge: number | null;
  min_charge: number | null;
  max_charge: number | null;
  median_charge: number | null;
  total_hospitals: number | null;
  consumer_name: string | null;
  route: string;
  codeType: string;
}

export const ConsumerSearch = () => {
  const { isDark } = useTheme();
  const T = isDark ? T_DARK : T_LIGHT;
  // "Near you" on the scenario result cards — the same block as the /scenarios
  // listing (Trent, 2026-09-29): location + radius shared with the rest of the
  // site, prompt-free approximate location first, the browser prompt as fallback.
  const { location: userLocation, isLocating, error: locationError, detectLocation, detectApproximate, lookupZip, clearLocation } = useUserLocation(false);
  const [radiusMiles, setRadiusMiles] = useState(120);
  const [nearTableOpen, setNearTableOpen] = useState<Set<string>>(new Set());
  const approxTried = useRef(false);
  // Locate only once scenario results are on screen — a hospital or code search never asks.
  const [scenarioCount, setScenarioCount] = useState(0);
  useEffect(() => {
    if (!scenarioCount || userLocation || approxTried.current) return;
    approxTried.current = true;
    detectApproximate().then((ok) => { if (!ok) detectLocation(); });
  }, [scenarioCount, userLocation, detectApproximate, detectLocation]);
  const { t } = useTranslation(['search', 'common']);
  const stickyHeaderOffset = useAhpHeaderOffset();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";
  const typeParam = (searchParams.get("searchType") || searchParams.get("type") || "scenario") as SearchCategory;

  const [query, setQuery] = useState(queryParam);
  const [searchCategory, setSearchCategory] = useState<SearchCategory>(
    SEARCH_CATEGORIES.some((c) => c.value === typeParam) ? typeParam : "scenario"
  );

  // Results state
  const [scenarioResults, setScenarioResults] = useState<ScenarioResult[]>([]);
  const [procedureResults, setProcedureResults] = useState<ProcedureResult[]>([]);
  const [bookmarked, setBookmarked] = useState<Set<string>>(() => new Set(scenarioStorage.getBookmarks()));
  const [collapsedHospitals, setCollapsedHospitals] = useState<Set<string>>(new Set());
  const [hospitalDealMode, setHospitalDealMode] = useState<Record<string, 'deal' | 'expensive'>>({});
  const toggleBookmark = (id: string) => {
    scenarioStorage.toggleBookmark(id);
    setBookmarked(new Set(scenarioStorage.getBookmarks()));
  };
  const [codeResults, setCodeResults] = useState<CodeResult[]>([]);
  const [hospitalResults, setHospitalResults] = useState<HospitalResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  // Hospital filter/sort state
  const [hospitalStateFilter, setHospitalStateFilter] = useState<string>("all");
  const [hospitalTypeFilter, setHospitalTypeFilter] = useState<string>("all");
  const [hospitalSort, setHospitalSort] = useState<"expensive" | "cheapest">("cheapest");
  const [hospitalDisplayLimit, setHospitalDisplayLimit] = useState(50);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const stateDropRef = useRef<HTMLDivElement>(null);
  const typeDropRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (stateDropRef.current && !stateDropRef.current.contains(e.target as Node)) {
        setStateDropdownOpen(false);
      }
      if (typeDropRef.current && !typeDropRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Update URL when search changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    params.set("searchType", searchCategory);
    setSearchParams(params, { replace: true });
  }, [query, searchCategory]);

  // Clear results when category changes
  useEffect(() => {
    setScenarioResults([]);
    setProcedureResults([]);
    setCodeResults([]);
    setHospitalResults([]);
    setFallbackNotice(null);
  }, [searchCategory]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setScenarioResults([]);
      setProcedureResults([]);
      setCodeResults([]);
      setHospitalResults([]);
      setFallbackNotice(null);
      return;
    }
    const timer = setTimeout(() => {
      runSearch(query, searchCategory);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchCategory]);

  const runSearch = async (q: string, category: SearchCategory) => {
    setIsSearching(true);
    setFallbackNotice(null);
    try {
      // Clear all result types first
      setScenarioResults([]);
      setProcedureResults([]);
      setCodeResults([]);
      setHospitalResults([]);

      let primaryCount = 0;
      switch (category) {
        case "scenario":
          primaryCount = await searchScenarios(q);
          break;
        case "hospital":
          primaryCount = searchHospitalsStatic(q);
          break;
        case "procedure":
          primaryCount = await searchProcedures(q);
          break;
        case "cpt":
        case "hcpcs":
        case "rev":
        case "ms-drg":
          await searchByCodeType(q, category);
          break;
      }

      // Code-shaped queries surface matching code pages no matter which
      // category pill is active — someone typing "99213" or "0450" with a
      // bill in hand wants /cpt/99213 or /revenue/0450, not a hospital list.
      if (!CODE_CATEGORIES.includes(category as any)) {
        const codeMatches = await detectCodeMatches(q);
        if (codeMatches.length > 0) {
          setCodeResults(codeMatches);
          setFallbackNotice(
            primaryCount > 0
              ? t('search:codeNotice.alsoMatches', { query: q.trim() })
              : t('search:codeNotice.looksLike', { query: q.trim() })
          );
        }
      }
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setIsSearching(false);
    }
  };

  // ── Hospital search (static rankings data) ──
  const searchHospitalsStatic = (q: string): number => {
    if (!allHospitalResults.length) {
      setHospitalResults([]);
      return 0;
    }
    const lower = q.toLowerCase();
    const matches = allHospitalResults.filter((h) => {
      const name = h.name.toLowerCase();
      const city = h.city.toLowerCase();
      const state = h.state.toLowerCase();
      return name.includes(lower) || city.includes(lower) || state.includes(lower);
    });
    setHospitalResults(matches);
    return matches.length;
  };

  // ── Scenario search (static-first) ──
  const searchScenarios = async (q: string): Promise<number> => {
    const lower = q.toLowerCase();

    // Try static data first
    if (allScenarios?.length) {
      const matches = allScenarios.filter((s) =>
        s.name.toLowerCase().includes(lower) ||
        s.description.toLowerCase().includes(lower)
      );
      if (matches.length > 0) {
        setScenarioResults(matches.slice(0, 20));
        return matches.length;
      }
    }

    // Supabase fallback
    try {
      const { data, error } = await supabase
        .from("scenarios")
        .select("id, name, description, icon_name, color, duration, total_procedures, phases, estimated_cost, severity_level")
        .eq("is_active", true)
        .or(`name.ilike.%${q}%,description.ilike.%${q}%`)
        .order("name")
        .limit(20);

      if (!error && data && data.length > 0) {
        const results: ScenarioResult[] = data.map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description || "",
          icon_name: s.icon_name || "Activity",
          color: s.color || T.red,
          duration: s.duration || "",
          total_procedures: s.total_procedures || 0,
          phases: s.phases || [],
          estimated_cost: s.estimated_cost || 0,
          real_total_cost: null,
        }));
        setScenarioResults(results);
        return results.length;
      }
    } catch (e) {
      console.warn("[ConsumerSearch] Supabase scenario search failed:", e);
    }

    setScenarioResults([]);
    return 0;
  };

  // ── Procedure search ──
  const searchProcedures = async (q: string): Promise<number> => {
    const { data, error } = await supabase
      .from("procedures")
      .select("code, code_type, name, consumer_friendly_description, llm_quick_name")
      .or(`code.ilike.%${q}%,name.ilike.%${q}%,consumer_friendly_description.ilike.%${q}%,llm_quick_name.ilike.%${q}%`)
      .limit(30);

    if (error) throw error;
    if (!data || data.length === 0) {
      setProcedureResults([]);
      return 0;
    }

    // Enrich with pricing from cpt_pricing_averages for CPT codes
    const cptCodes = data.filter((p) => p.code_type === "CPT").map((p) => p.code);
    let pricingMap: Record<string, { avg: number | null; hospitals: number | null }> = {};

    if (cptCodes.length > 0) {
      const { data: pricing } = await supabase
        .from("cpt_pricing_averages")
        .select("cpt_code, avg_gross_charge, total_hospitals")
        .in("cpt_code", cptCodes)
        .eq("setting_type", "all_settings")
        .order("total_hospitals", { ascending: false, nullsFirst: false });

      // First row per code has highest total_hospitals (ordered desc)
      for (const p of pricing || []) {
        if (!pricingMap[p.cpt_code]) {
          pricingMap[p.cpt_code] = { avg: p.avg_gross_charge, hospitals: p.total_hospitals };
        }
      }
    }

    setProcedureResults(
      data.map((p) => ({
        code: p.code,
        code_type: p.code_type || "CPT",
        name: p.name,
        consumer_friendly_description: p.consumer_friendly_description,
        llm_quick_name: p.llm_quick_name,
        avg_gross_charge: pricingMap[p.code]?.avg ?? null,
        total_hospitals: pricingMap[p.code]?.hospitals ?? null,
      }))
    );
    return data.length;
  };

  // ── Code-type search (CPT, HCPCS, REV, MS-DRG) ──
  const searchByCodeType = async (q: string, type: SearchCategory) => {
    const config = CODE_TYPE_CONFIG[type];
    if (!config) return;

    // Uppercase for the exact probe (eq is case-sensitive; codes are stored
    // uppercase) and strip any user-typed "MS-" so we don't double-prefix.
    const trimmed = q.trim().toUpperCase();
    const bare = trimmed.replace(/^MS-?/, "");
    const selectCols = `${config.codeCol}, ${config.descCol}, ${config.avgChargeCol}, ${config.minChargeCol}, ${config.maxChargeCol}, ${config.medianChargeCol}, total_hospitals`;

    // For DRG, codes are stored with "MS-" prefix (e.g. "MS-470")
    const exactCode = config.codePrefix ? `${config.codePrefix}${bare}` : trimmed;

    // Try exact match first, then fuzzy
    // Order by total_hospitals desc so we can deduplicate keeping the most-reported row
    let query1 = supabase
      .from(config.table)
      .select(selectCols)
      .eq(config.codeCol, exactCode)
      .order("total_hospitals", { ascending: false, nullsFirst: false });
    if (config.hasSettingType) query1 = query1.eq("setting_type", "all_settings");
    let { data, error } = await query1.limit(50);

    if (error) throw error;

    // If no exact match, try fuzzy (search both with and without prefix)
    if (!data || data.length === 0) {
      const fuzzyTerm = config.codePrefix ? `${config.codePrefix}${bare}` : trimmed;
      let query2 = supabase
        .from(config.table)
        .select(selectCols)
        .or(`${config.codeCol}.ilike.%${fuzzyTerm}%,${config.descCol}.ilike.%${trimmed}%`)
        .order("total_hospitals", { ascending: false, nullsFirst: false });
      if (config.hasSettingType) query2 = query2.eq("setting_type", "all_settings");
      const fuzzyRes = await query2.limit(60);

      if (fuzzyRes.error) throw fuzzyRes.error;
      data = fuzzyRes.data;
    }

    if (!data || data.length === 0) {
      setCodeResults([]);
      return;
    }

    // Fetch consumer-friendly names from procedures table for CPT/HCPCS
    let nameMap: Record<string, string> = {};
    if (type === "cpt" || type === "hcpcs") {
      const codes = data.map((r: any) => r[config.codeCol]);
      const uniqueCodes = [...new Set(codes)];
      const { data: procs } = await supabase
        .from("procedures")
        .select("code, llm_quick_name")
        .in("code", uniqueCodes);

      for (const p of procs || []) {
        if (p.llm_quick_name) nameMap[p.code] = p.llm_quick_name;
      }
    }

    // Deduplicate by code — keep the row with the most hospitals (data is already sorted desc)
    const seen = new Set<string>();
    const deduped = data.filter((r: any) => {
      const key = String(r[config.codeCol]);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    setCodeResults(
      deduped.map((r: any) => ({
        code: r[config.codeCol],
        description: r[config.descCol] || "",
        avg_charge: r[config.avgChargeCol],
        min_charge: r[config.minChargeCol],
        max_charge: r[config.maxChargeCol],
        median_charge: r[config.medianChargeCol],
        total_hospitals: r.total_hospitals ?? null,
        consumer_name: nameMap[r[config.codeCol]] || null,
        route: config.route,
        codeType: type,
      }))
    );
  };

  // ── Cross-category code detection ──
  // Exact-match probe across all four pricing tables for code-shaped queries,
  // run regardless of which category pill is active. Rows are ordered by
  // hospital count and deduped per code so the most-reported description wins
  // (REV codes have one row per billing unit — without the ordering + dedup,
  // "0450" surfaced arbitrary per-unit rows with drug descriptions).
  const detectCodeMatches = async (q: string): Promise<CodeResult[]> => {
    const trimmed = q.trim().toUpperCase();
    const looksLikeCode =
      /^\d{2,5}$/.test(trimmed) ||        // 99213, 0450, 470
      /^[A-Z]\d{3,4}$/.test(trimmed) ||   // J1885, A0428, G0121
      /^\d{4}[A-Z]$/.test(trimmed) ||     // 0042T (CPT Cat III)
      /^MS-?\d{3}$/.test(trimmed);        // MS-470 / MS470
    if (!looksLikeCode) return [];
    const bare = trimmed.replace(/^MS-?/, "");

    const perTable = await Promise.all(
      Object.entries(CODE_TYPE_CONFIG).map(async ([type, config]) => {
        const selectCols = `${config.codeCol}, ${config.descCol}, ${config.avgChargeCol}, ${config.minChargeCol}, ${config.maxChargeCol}, ${config.medianChargeCol}, total_hospitals`;
        const exactCode = config.codePrefix ? `${config.codePrefix}${bare}` : trimmed;

        let query = supabase
          .from(config.table)
          .select(selectCols)
          .eq(config.codeCol, exactCode)
          .order("total_hospitals", { ascending: false, nullsFirst: false });
        if (config.hasSettingType) query = query.eq("setting_type", "all_settings");
        const { data } = await query.limit(5);

        return { type, config, data: data || [] };
      })
    );

    const matches: CodeResult[] = [];
    for (const { type, config, data } of perTable) {
      const seen = new Set<string>();
      for (const r of data) {
        const key = String((r as any)[config.codeCol]);
        if (seen.has(key)) continue;
        seen.add(key);
        matches.push({
          code: (r as any)[config.codeCol],
          description: (r as any)[config.descCol] || "",
          avg_charge: (r as any)[config.avgChargeCol],
          min_charge: (r as any)[config.minChargeCol],
          max_charge: (r as any)[config.maxChargeCol],
          median_charge: (r as any)[config.medianChargeCol],
          total_hospitals: (r as any).total_hospitals ?? null,
          consumer_name: null,
          route: config.route,
          codeType: type,
        });
      }
    }

    // Consumer-friendly names for CPT/HCPCS hits ("Office visit" beats
    // "OFFICE/OUTPATIENT ESTABLISHED LOW MDM 20-29 MIN")
    const namedCodes = matches
      .filter((m) => m.codeType === "cpt" || m.codeType === "hcpcs")
      .map((m) => m.code);
    if (namedCodes.length > 0) {
      const { data: procs } = await supabase
        .from("procedures")
        .select("code, llm_quick_name")
        .in("code", [...new Set(namedCodes)]);
      const nameMap: Record<string, string> = {};
      for (const p of procs || []) {
        if (p.llm_quick_name) nameMap[p.code] = p.llm_quick_name;
      }
      for (const m of matches) {
        if (nameMap[m.code]) m.consumer_name = nameMap[m.code];
      }
    }

    return matches;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query) {
      trackSearch(query, searchCategory);
      runSearch(query, searchCategory);
    }
  };

  // Load all rankings from static JSON (used for default view + hospital search)
  const { data: allRankings, isLoading: isLoadingRankings } = useQuery({
    queryKey: ["consumer-rankings-static"],
    queryFn: async () => {
      const staticData = await loadStaticData<{ data: any[] }>('rankings.json');
      if (staticData?.data?.length) {
        console.log(`[ConsumerSearch] Using static rankings (${staticData.data.length})`);
        return staticData.data;
      }
      // Supabase fallback
      const { data, error } = await supabase
        .from("hospital_ranking_summary")
        .select("hospital_id, avg_gross_markup_percentage, median_gross_markup_percentage, overall_rank_gross, total_procedures, outlier_expensive_count, shame_score_calculated, hospital_name, hospital_city, hospital_state, has_chargemaster_errors")
        .not("avg_gross_markup_percentage", "is", null)
        .or("exclude_from_rankings.is.null,exclude_from_rankings.eq.false")
        .order("median_gross_markup_percentage", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 60,
  });

  // Derive hospitals from rankings — filter out hospitals with < 10 procedures (unreliable data)
  const allHospitalResults: HospitalResult[] = useMemo(() =>
    (allRankings || [])
      .filter((r: any) => (r.total_procedures || 0) >= 10)
      .map((r: any) => ({
        id: r.hospital_id,
        name: r.hospital_name || "Unknown",
        city: r.hospital_city || "",
        state: r.hospital_state || "",
        avg_gross_markup_percentage: r.avg_gross_markup_percentage,
        median_gross_markup_percentage: r.median_gross_markup_percentage,
        total_procedures: r.total_procedures,
        outlier_expensive_count: r.outlier_expensive_count,
        shame_rank: r.overall_rank_gross || 0,
        hospital_type: r.hospital_type || null,
        scenario_count: r.scenario_count || 0,
      })),
    [allRankings]
  );
  // Show top 25 for non-hospital categories, all for hospital category
  const topHospitals = searchCategory === "hospital"
    ? allHospitalResults
    : allHospitalResults.slice(0, 25);
  const isLoadingTop = isLoadingRankings;

  // Load all scenarios from static JSON (used for default view + scenario search)
  const { data: allScenarios, isLoading: isLoadingScenarios } = useQuery({
    queryKey: ["consumer-scenarios-search"],
    queryFn: async (): Promise<ScenarioResult[]> => {
      // Use the lightweight index (~18MB, card-ready: name/desc/cost/phaseBreakdown)
      // instead of the full scenarios.json (~75MB with per-procedure detail). The
      // search list + featured cards only need index fields, and the index also
      // contains base scenarios the full file was missing. Huge native-landing win.
      const staticData = await loadTranslatedStaticData<{ data: any[] }>('scenarios_index.json');
      if (staticData?.data?.length) {
        // Filter out catalog-hidden scenarios (e.g., DRG base scenarios used only for hospital pricing)
        const visible = staticData.data.filter((s: any) => !s.hidden);
        console.log(`[ConsumerSearch] Using static scenarios (${visible.length} visible / ${staticData.data.length} total)`);
        // Count variants per scenarioFamilyId
        const familyCount = new Map<string, number>();
        for (const s of visible) {
          const fid = s.scenarioFamilyId || s.scenario_family_id;
          if (fid) familyCount.set(fid, (familyCount.get(fid) || 0) + 1);
        }
        return visible.map((s: any) => ({
          id: s.id || s.uuid,
          name: s.name,
          description: s.description || "",
          icon_name: s.iconName || s.icon_name || "Activity",
          color: s.color || T.red,
          duration: s.duration || "",
          total_procedures: s.totalProcedures || s.total_procedures || 0,
          phases: s.phases || [],
          estimated_cost: s.estimatedCost || s.estimated_cost || 0,
          real_total_cost: s.realTotalCost || s.real_total_cost || null,
          // National spread of complete bills (scenarios_index.json) for the card's band.
          p10_cost: s.p10Cost ?? null,
          median_cost: s.estimatedCostMedian ?? s.medianCost ?? null,
          p90_cost: s.p90Cost ?? null,
          severity_level: s.severityLevel ?? s.severity_level ?? null,
          scenario_family_id: s.scenarioFamilyId || s.scenario_family_id || null,
          variant_count: (s.scenarioFamilyId || s.scenario_family_id) ? familyCount.get(s.scenarioFamilyId || s.scenario_family_id) || 1 : 1,
          phaseBreakdown: s.phaseBreakdown || s.phase_breakdown || {},
          tags: s.tags || [],
          created_by: s.createdBy || s.created_by || null,
          is_verified: s.isVerified ?? s.is_verified ?? false,
        }));
      }
      // Supabase fallback
      const { data, error } = await supabase
        .from("scenarios")
        .select("id, name, description, icon_name, color, duration, total_procedures, phases, estimated_cost, severity_level")
        .eq("is_active", true)
        .eq("hidden_from_catalog", false)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description || "",
        icon_name: s.icon_name || "Activity",
        color: s.color || T.red,
        duration: s.duration || "",
        total_procedures: s.total_procedures || 0,
        phases: s.phases || [],
        estimated_cost: s.estimated_cost || 0,
        real_total_cost: null,
      }));
    },
    staleTime: 1000 * 60 * 60,
  });

  // Re-run a DEEP-LINKED search once the static data finishes loading. The
  // debounced search effect above fires ~300ms after mount — for a search
  // opened via a URL like /search?q=cleveland&searchType=hospital (shared
  // link, bookmark, autocomplete nav), rankings.json (~10MB) / scenarios
  // aren't loaded yet, so hospital/scenario searches returned "No results
  // found" for entries that exist ("mayo", "cleveland") and never re-ran.
  // This fires when the relevant dataset becomes available. Placed here (not
  // in the debounced effect's deps) because allHospitalResults/allScenarios
  // are declared below that effect — referencing them there would TDZ-crash.
  const hospCount = allHospitalResults.length;
  const scenCount = allScenarios?.length ?? 0;
  useEffect(() => {
    if (!query || query.length < 2) return;
    if (searchCategory === "hospital" && hospCount > 0) runSearch(query, searchCategory);
    else if (searchCategory === "scenario" && scenCount > 0) runSearch(query, searchCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospCount, scenCount]);

  const featuredScenarios = React.useMemo(() => (
    (allScenarios || [])
      // Base / standalone only — lead with each family's base, not a severity variant
      .filter((s) => !s.scenario_family_id || (s.severity_level ?? 0) === 0)
      .sort((a, b) => {
        const pa = consumerPrio(a.name), pb = consumerPrio(b.name);
        if (pa !== pb) return pa - pb;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 20)
  ), [allScenarios]);

  // Available states from rankings data
  const availableStates = React.useMemo(() => {
    if (!allRankings?.length) return [];
    const states = new Set<string>();
    for (const r of allRankings) {
      if (r.hospital_state) states.add(r.hospital_state);
    }
    return Array.from(states).sort();
  }, [allRankings]);

  // Available hospital types from rankings data
  const availableTypes = React.useMemo(() => {
    if (!allRankings?.length) return [];
    const types = new Set<string>();
    for (const r of allRankings) {
      if (r.hospital_type) types.add(r.hospital_type);
    }
    return Array.from(types).sort();
  }, [allRankings]);

  // Use max rank from data (not array length) since DB may have more ranked hospitals than the static JSON
  const totalRankedHospitals = useMemo(() => {
    if (!allRankings?.length) return 0;
    let maxRank = 0;
    for (const r of allRankings) {
      if (r.overall_rank_gross > maxRank) maxRank = r.overall_rank_gross;
    }
    return maxRank || allRankings.length;
  }, [allRankings]);

  // Determine what to show
  const hasQuery = query.length >= 2;
  const showScenarios = searchCategory === "scenario"
    ? (hasQuery ? scenarioResults : featuredScenarios || [])
    : [];
  useEffect(() => { setScenarioCount(showScenarios.length); }, [showScenarios.length]);

  // Hospital results with state filter + sort applied. Code categories never
  // browse-render the hospital list — picking "CPT code" and seeing hospitals
  // was the top confusion in launch review (Master 2026-07-12).
  const isCodeCategory = CODE_CATEGORIES.includes(searchCategory as any);
  const rawHospitals = searchCategory === "hospital"
    ? (hasQuery ? hospitalResults : topHospitals)
    : (!hasQuery && searchCategory === "procedure" ? topHospitals : []);

  const showHospitals = React.useMemo(() => {
    let filtered = [...rawHospitals];
    // State filter
    if (hospitalStateFilter !== "all") {
      filtered = filtered.filter((h) => h.state === hospitalStateFilter);
    }
    // Hospital type filter
    if (hospitalTypeFilter !== "all") {
      filtered = filtered.filter((h) => (h.hospital_type || "Unspecified") === hospitalTypeFilter);
    }
    // Sort — hospitals without scenario data sort to bottom (no useful cards to show users)
    const hasScenarios = (h: HospitalResult) => (h.scenario_count || 0) > 0;
    // Sort on the ranked metric (median-baseline markup) so the order agrees
    // with the displayed rank pills; avg is the back-compat fallback.
    const rankMetric = (h: HospitalResult) => h.median_gross_markup_percentage ?? h.avg_gross_markup_percentage ?? 0;
    if (hospitalSort === "cheapest") {
      filtered.sort((a, b) => {
        const aHas = hasScenarios(a) ? 0 : 1;
        const bHas = hasScenarios(b) ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        return rankMetric(a) - rankMetric(b);
      });
    } else {
      filtered.sort((a, b) => {
        const aHas = hasScenarios(a) ? 0 : 1;
        const bHas = hasScenarios(b) ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        return rankMetric(b) - rankMetric(a);
      });
    }
    return filtered;
  }, [rawHospitals, hospitalStateFilter, hospitalTypeFilter, hospitalSort]);

  const isLoading = hasQuery ? isSearching : (searchCategory === "scenario" ? isLoadingScenarios : isLoadingTop);

  const formatPrice = (val: number | null) => {
    if (val == null) return "—";
    return `$${Math.round(val).toLocaleString()}`;
  };

  // NOTE: the root <div> below is intentionally NOT `position: relative`. The
  // flag lives in a single global fixed layer (z-index 0); a positioned root
  // with an opaque background would paint over it and hide the flag. A static
  // root keeps the background below the global video so the flag shows through
  // (this matches the Scenarios page).
  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: T.bg, color: T.text, fontFamily: sans }}>
      <FlagBackground dark={isDark} />
      <div className="relative" style={{ zIndex: 1 }}>

      {/* ─── HEADER ─── (compact on native; it scrolls away above the sticky bar) */}
      <section style={{ paddingTop: isNativeApp() ? 20 : "clamp(48px, 8vw, 100px)", paddingBottom: isNativeApp() ? 16 : "clamp(32px, 4vw, 56px)" }}>
        <div className="max-w-[1260px] mx-auto px-6 sm:px-12 text-center">
          <h1
            onClick={tunerTap}
            className="leading-[1.05] tracking-tight mb-6"
            style={{
              fontFamily: serif,
              fontSize: "clamp(32px, 5vw, 64px)",
              letterSpacing: "-1px",
              fontWeight: 400,
              color: T.text,
            }}
          >
            {t('search:hero.title', { defaultValue: 'Search Hospital Prices' })}
          </h1>
          <p style={{ fontSize: "clamp(16px, 2vw, 20px)", color: T.textMuted, lineHeight: 1.6, maxWidth: 600, margin: "0 auto" }}>
            {t('search:hero.subtitle', { defaultValue: 'Find and compare real prices from thousands of hospitals. Search by scenario, procedure, or billing code.' })}
          </p>
        </div>
      </section>

      {/* ─── SEARCH BAR ─── On native this section sticks under the global header
          (its parent is the page root, so it pins for the full scroll). The hero
          above scrolls away first → search reveals on scroll, then stays put. */}
      <section
        className="max-w-[800px] mx-auto px-6 sm:px-12"
        style={isNativeApp()
          ? {
              position: "sticky",
              // Measured header height (includes the safe-area inset) — was a
              // hardcoded 67px+env() that opened a gap when the header slimmed.
              top: `${stickyHeaderOffset}px`,
              zIndex: 40,
              // Frosted, translucent band (not solid T.bg) so the flag video shows
              // through and blends evenly with the rest of the page — while the
              // blur keeps content scrolling underneath readable behind the bar.
              backgroundColor: isDark ? "rgba(var(--civic-bg-dark-rgb),0.55)" : "rgba(var(--civic-cream-rgb),0.55)",
              backdropFilter: "blur(14px) saturate(160%)",
              WebkitBackdropFilter: "blur(14px) saturate(160%)",
              paddingTop: 10,
              paddingBottom: 14,
            }
          : { marginBottom: "clamp(24px, 3vw, 40px)" }}
      >
        <form onSubmit={handleSubmit}>
          <div
            className="flex flex-row items-stretch"
            style={{
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              backgroundColor: T.cardBg,
              boxShadow: `0 4px 24px ${T.shadow}`,
              overflow: "hidden",
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 pl-4 sm:pl-5">
              {/* Field glass — desktop only. On mobile the compact red button on
                  the right carries the magnifier, so a second one here is noise. */}
              <Search className="w-5 h-5 flex-shrink-0 hidden sm:block" style={{ color: T.textMuted }} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  searchCategory === "cpt" ? t('search:codePlaceholder.cpt')
                  : searchCategory === "hcpcs" ? t('search:codePlaceholder.hcpcs')
                  : searchCategory === "rev" ? t('search:codePlaceholder.rev')
                  : searchCategory === "ms-drg" ? t('search:codePlaceholder.msDrg')
                  : t('search:placeholder')
                }
                aria-label={t('search:placeholder')}
                autoFocus={!isNativeApp()}
                className="w-full py-4 outline-none bg-transparent min-w-0"
                style={{ fontFamily: sans, fontSize: 17, color: T.text }}
              />
            </div>
            <button
              type="submit"
              aria-label={t('common:actions.search')}
              className="flex items-center justify-center self-stretch w-11 px-0 sm:w-auto sm:px-8 m-1.5 rounded-lg font-medium transition-opacity hover:opacity-90 active:opacity-80"
              style={{
                backgroundColor: T.red,
                color: "#fff",
                fontFamily: sans,
                fontSize: 15,
                letterSpacing: "0.5px",
                flexShrink: 0,
              }}
            >
              {/* Mobile: compact magnifier. Desktop: full SEARCH label. */}
              <Search className="w-5 h-5 sm:hidden" strokeWidth={2.5} />
              <span className="hidden sm:inline whitespace-nowrap">{t('common:actions.search')}</span>
            </button>
          </div>
        </form>

        {/* Search type — consumer-first. Two plain pills (Scenario, Hospital)
            plus ONE "Medical code" dropdown for people looking up a specific
            code off a bill. The raw CPT/HCPCS/REV/MS-DRG/Procedure pills meant
            nothing to most consumers, so they're folded into the dropdown. */}
        <div className="flex flex-nowrap justify-center items-center gap-1.5 sm:gap-2 mt-5">
          {SEARCH_CATEGORIES.filter((c) => c.value === "scenario" || c.value === "hospital").map((cat) => (
            <button
              key={cat.value}
              onClick={() => setSearchCategory(cat.value as SearchCategory)}
              className="px-3 sm:px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap shrink-0"
              style={{
                backgroundColor: searchCategory === cat.value ? T.blue : "transparent",
                color: searchCategory === cat.value ? "#fff" : T.textMuted,
                border: searchCategory === cat.value ? "none" : `1px solid ${T.border}`,
                fontFamily: sans,
              }}
            >
              {t(cat.labelKey)}
            </button>
          ))}
          {(() => {
            const CODE_TYPES = ["cpt", "hcpcs", "rev", "ms-drg"];
            const isCode = CODE_TYPES.includes(searchCategory);
            return (
              <select
                value={isCode ? searchCategory : ""}
                onChange={(e) => setSearchCategory(e.target.value as SearchCategory)}
                aria-label={t('search:codeSelect.ariaLabel')}
                className="px-3 sm:px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                style={{
                  backgroundColor: isCode ? T.blue : "transparent",
                  color: isCode ? "#fff" : T.textMuted,
                  border: isCode ? "none" : `1px solid ${T.border}`,
                  fontFamily: sans,
                }}
              >
                <option value="" disabled>{t('search:codeSelect.placeholder')}</option>
                <option value="cpt">{t('search:codeSelect.cpt')}</option>
                <option value="hcpcs">{t('search:codeSelect.hcpcs')}</option>
                <option value="rev">{t('search:codeSelect.rev')}</option>
                <option value="ms-drg">{t('search:codeSelect.msDrg')}</option>
              </select>
            );
          })()}
        </div>
      </section>

      {/* ─── RESULTS ─── */}
      <section className="max-w-[1260px] mx-auto px-6 sm:px-12">

        {/* Fallback notice */}
        {fallbackNotice && (
          <div
            className="flex items-center gap-3 px-5 py-3.5 mb-6 rounded-lg"
            style={{ backgroundColor: "rgba(var(--civic-gold-rgb),0.10)", border: `1px solid rgba(var(--civic-gold-rgb),0.25)` }}
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: T.gold }} />
            <p style={{ fontSize: 14, color: T.text }}>{fallbackNotice}</p>
          </div>
        )}

        {/* Section heading */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6">
          <h2
            style={{
              fontFamily: serif,
              fontSize: "clamp(24px, 3.5vw, 40px)",
              fontWeight: 400,
              letterSpacing: "-0.5px",
            }}
          >
            {hasQuery
              ? (searchCategory === "hospital"
                  ? t('search:resultsHeader.hospitalsMatching', { query })
                  : t('search:resultsHeader.resultsFor', { query }))
              : searchCategory === "scenario"
              ? t('search:resultsHeader.featured')
              : isCodeCategory
              ? t('search:resultsHeader.lookup')
              : hospitalSort === "cheapest"
              ? t('search:resultsHeader.bestValue')
              : t('search:resultsHeader.mostExpensive')}
          </h2>
          {!hasQuery && (searchCategory === "hospital" || searchCategory !== "scenario") && showHospitals.length > 0 && (
            <Link
              to="/hospital-rankings"
              className="flex items-center gap-1 transition-colors mt-2 sm:mt-0"
              style={{ color: T.red, fontSize: 15, fontFamily: sans }}
            >
              {t('common:actions.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-20 gap-3" style={{ color: T.textMuted }}>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span style={{ fontFamily: sans, fontSize: 16 }}>{t('search:loading.searching')}</span>
          </div>
        )}

        {/* ── CODE RESULTS (CPT, HCPCS, REV, MS-DRG) ── */}
        {!isLoading && codeResults.length > 0 && (
          <div>
            {codeResults.map((result, i) => {
              // Clean display code: strip "MS-" prefix for DRG since badge already says "MS-DRG"
              const displayCode = result.codeType === "ms-drg" && result.code.startsWith("MS-")
                ? result.code.slice(3)
                : result.code;

              return (
                <Link
                  key={`${result.codeType}-${result.code}-${i}`}
                  to={`${result.route}/${result.code}`}
                  className="block transition-colors"
                  style={{
                    borderBottom: `1px solid ${T.border}`,
                    padding: "clamp(14px, 2vw, 24px) 0",
                    textDecoration: "none",
                    color: T.text,
                    ...(i === 0 ? { borderTop: `1px solid ${T.border}` } : {}),
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Left: code badge + description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
                          style={{
                            backgroundColor: isDark ? "rgba(91,155,213,0.12)" : "rgba(var(--civic-navy-rgb),0.08)",
                            color: T.blue,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}
                        >
                          {result.codeType.toUpperCase()} {displayCode}
                        </span>
                        <h3
                          className="truncate"
                          style={{ fontFamily: serif, fontSize: "clamp(17px, 2vw, 22px)", fontWeight: 400 }}
                        >
                          {result.consumer_name || result.description}
                        </h3>
                      </div>
                      {result.consumer_name && result.description && (
                        <p className="line-clamp-1" style={{ fontSize: 13, color: T.textMuted }}>
                          {result.description}
                        </p>
                      )}
                    </div>

                    {/* Right: pricing */}
                    <div className="flex items-center gap-5 flex-shrink-0">
                      <div className="text-right">
                        <p style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.4px", textTransform: "uppercase" }}>
                          {t('search:results.avgCharge')}
                        </p>
                        <p style={{
                          fontFamily: serif,
                          fontSize: "clamp(20px, 2.5vw, 30px)",
                          color: T.red,
                          fontWeight: 400,
                        }}>
                          {formatPrice(result.avg_charge)}
                        </p>
                      </div>
                      <div className="hidden sm:block text-right" style={{ minWidth: 80 }}>
                        <p style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.4px", textTransform: "uppercase" }}>
                          {t('search:results.priceRange')}
                        </p>
                        <p style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>
                          {formatPrice(result.min_charge)} – {formatPrice(result.max_charge)}
                        </p>
                      </div>
                      {result.total_hospitals != null && (
                        <div className="hidden sm:block text-right" style={{ minWidth: 60 }}>
                          <p style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.4px", textTransform: "uppercase" }}>
                            {t('search:results.hospitals')}
                          </p>
                          <p style={{ fontSize: 13, color: T.blue }}>
                            {result.total_hospitals.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* ── SCENARIO RESULTS ── */}
        {!isLoading && showScenarios.length > 0 && (
          <>
          <div className="mb-4">
            <LocationChooser
              currentLocation={userLocation}
              onDetectLocation={detectLocation}
              onZipLookup={lookupZip}
              onClearLocation={clearLocation}
              isLocating={isLocating}
              error={locationError}
              radiusMiles={radiusMiles}
              onRadiusChange={setRadiusMiles}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {showScenarios.map((scenario) => {
              const cardData: ScenarioCardData = {
                id: scenario.id,
                name: scenario.name,
                description: scenario.description,
                icon_name: scenario.icon_name,
                color: scenario.color,
                duration: scenario.duration,
                total_procedures: scenario.total_procedures,
                phases: scenario.phases,
                severity_level: scenario.severity_level ?? null,
                calculated_avg_cost: scenario.real_total_cost || scenario.estimated_cost || 0,
                phaseBreakdown: scenario.phaseBreakdown || {},
                phaseProcedures: {},
                tags: scenario.tags || [],
                created_by: scenario.created_by ?? null,
                is_verified: scenario.is_verified ?? false,
              };
              // Build family siblings list from allScenarios (excluding self).
              // Threads estimatedCost + totalProcedures so the ScenarioFamilyModal
              // opened from this card's bottom severity bar renders real
              // per-variant prices and proc counts (otherwise the modal
              // falls back to 0s).
              const familySiblings = scenario.scenario_family_id
                ? (allScenarios || [])
                    .filter(s => s.scenario_family_id === scenario.scenario_family_id && s.id !== scenario.id)
                    .map(s => ({
                      id: s.id,
                      severity_level: s.severity_level ?? null,
                      name: s.name,
                      estimatedCost: s.real_total_cost ?? s.estimated_cost ?? null,
                      totalProcedures: s.total_procedures ?? null,
                    }))
                : [];
              return (
                <div key={scenario.id} className={`[&>*]:h-full ${nearTableOpen.has(scenario.id) ? 'md:col-span-2' : ''}`}>
                <ScenarioCard
                  scenario={cardData}
                  isStarred={bookmarked.has(scenario.id)}
                  theme={T as CardTheme}
                  onToggleBookmark={toggleBookmark}
                  familySiblings={familySiblings}
                  nearYou={
                    <ScenarioCardNearYou
                      scenarioId={scenario.id}
                      nationalTotal={scenario.estimated_cost || scenario.real_total_cost || 0}
                      band={{ p10: (scenario as any).p10_cost ?? null, median: (scenario as any).median_cost ?? null, avg: scenario.estimated_cost || null, p90: (scenario as any).p90_cost ?? null }}
                      location={userLocation}
                      radius={radiusMiles}
                      dark={isDark}
                      onTableToggle={(open) => setNearTableOpen((prev) => { const n = new Set(prev); if (open) n.add(scenario.id); else n.delete(scenario.id); return n; })}
                    />
                  }
                />
                </div>
              );
            })}
          </div>
          </>
        )}

        {/* ── PROCEDURE RESULTS ── */}
        {!isLoading && procedureResults.length > 0 && (
          <div>
            {procedureResults.map((proc, i) => {
              const codeTypeLower = proc.code_type.toLowerCase();
              const route = codeTypeLower === "cpt" ? "/cpt" : codeTypeLower === "hcpcs" ? "/hcpcs" : "/cpt";
              return (
                <Link
                  key={`${proc.code}-${proc.code_type}-${i}`}
                  to={`${route}/${proc.code}`}
                  className="block transition-colors"
                  style={{
                    borderBottom: `1px solid ${T.border}`,
                    padding: "clamp(14px, 2vw, 24px) 0",
                    textDecoration: "none",
                    color: T.text,
                    ...(i === 0 ? { borderTop: `1px solid ${T.border}` } : {}),
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: isDark ? "rgba(91,155,213,0.12)" : "rgba(var(--civic-navy-rgb),0.08)",
                            color: T.blue,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}
                        >
                          {proc.code_type} {proc.code}
                        </span>
                        <h3 style={{ fontFamily: serif, fontSize: "clamp(17px, 2vw, 22px)", fontWeight: 400 }}>
                          {proc.llm_quick_name || proc.name}
                        </h3>
                      </div>
                      {proc.consumer_friendly_description && (
                        <p className="line-clamp-1" style={{ fontSize: 13, color: T.textMuted }}>
                          {proc.consumer_friendly_description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-baseline gap-4 sm:text-right flex-shrink-0">
                      {proc.avg_gross_charge != null && (
                        <span style={{ fontFamily: serif, fontSize: "clamp(20px, 2.5vw, 30px)", color: T.red, fontWeight: 400 }}>
                          {formatPrice(proc.avg_gross_charge)}
                        </span>
                      )}
                      {proc.total_hospitals != null && (
                        <span style={{ fontSize: 13, color: T.textMuted }}>
                          {t('search:results.totalHospitals', { count: proc.total_hospitals.toLocaleString() })}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}


        {/* ── HOSPITAL RESULTS (default non-scenario view) ── */}
        {!isLoading && (showHospitals.length > 0 || (searchCategory === "hospital" && rawHospitals.length > 0)) && (
          <div>
            {/* Sort / Filter bar */}
            <div className="flex flex-wrap items-center gap-3 mb-4 pb-4" style={{ borderBottom: `1px solid ${T.border}` }}>
              {/* Sort toggle */}
              <button
                onClick={() => { setHospitalSort(hospitalSort === "expensive" ? "cheapest" : "expensive"); setHospitalDisplayLimit(50); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: hospitalSort === "cheapest"
                    ? (isDark ? "rgba(var(--civic-money-rgb),0.15)" : "rgba(var(--civic-money-rgb),0.08)")
                    : (isDark ? "rgba(181,0,0,0.12)" : "rgba(181,0,0,0.06)"),
                  color: hospitalSort === "cheapest" ? T.green : T.red,
                  fontFamily: sans,
                  border: `1px solid ${hospitalSort === "cheapest" ? T.green : T.red}`,
                }}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                {hospitalSort === "cheapest" ? "Best Value First" : "Most Expensive First"}
              </button>

              {/* State filter dropdown */}
              <div ref={stateDropRef} className="relative">
                <button
                  onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: hospitalStateFilter !== "all"
                      ? (isDark ? "rgba(var(--civic-red-rgb),0.15)" : "rgba(var(--civic-red-rgb),0.08)")
                      : "transparent",
                    color: hospitalStateFilter !== "all" ? T.red : T.textMuted,
                    fontFamily: sans,
                    border: `1px solid ${hospitalStateFilter !== "all" ? T.red : T.border}`,
                  }}
                >
                  <Filter className="w-3.5 h-3.5" />
                  {hospitalStateFilter === "all" ? "All States" : hospitalStateFilter}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {stateDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 rounded-lg shadow-lg overflow-y-auto"
                    style={{
                      backgroundColor: T.cardBg,
                      border: `1px solid ${T.border}`,
                      maxHeight: 280,
                      minWidth: 140,
                      zIndex: 50,
                    }}
                  >
                    <button
                      onClick={() => { setHospitalStateFilter("all"); setStateDropdownOpen(false); setHospitalDisplayLimit(50); }}
                      className="w-full text-left px-3 py-2 text-sm transition-colors"
                      style={{
                        color: hospitalStateFilter === "all" ? T.red : T.text,
                        fontWeight: hospitalStateFilter === "all" ? 600 : 400,
                        fontFamily: sans,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      All States
                    </button>
                    {availableStates.map((st) => (
                      <button
                        key={st}
                        onClick={() => { setHospitalStateFilter(st); setStateDropdownOpen(false); setHospitalDisplayLimit(50); }}
                        className="w-full text-left px-3 py-2 text-sm transition-colors"
                        style={{
                          color: hospitalStateFilter === st ? T.red : T.text,
                          fontWeight: hospitalStateFilter === st ? 600 : 400,
                          fontFamily: sans,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Hospital type filter dropdown */}
              <div ref={typeDropRef} className="relative">
                <button
                  onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: hospitalTypeFilter !== "all"
                      ? (isDark ? "rgba(var(--civic-red-rgb),0.15)" : "rgba(var(--civic-red-rgb),0.08)")
                      : "transparent",
                    color: hospitalTypeFilter !== "all" ? T.red : T.textMuted,
                    fontFamily: sans,
                    border: `1px solid ${hospitalTypeFilter !== "all" ? T.red : T.border}`,
                  }}
                >
                  <Filter className="w-3.5 h-3.5" />
                  {hospitalTypeFilter === "all" ? "All Types" : hospitalTypeFilter}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {typeDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 rounded-lg shadow-lg overflow-y-auto"
                    style={{
                      backgroundColor: T.cardBg,
                      border: `1px solid ${T.border}`,
                      maxHeight: 280,
                      minWidth: 200,
                      zIndex: 50,
                    }}
                  >
                    <button
                      onClick={() => { setHospitalTypeFilter("all"); setTypeDropdownOpen(false); setHospitalDisplayLimit(50); }}
                      className="w-full text-left px-3 py-2 text-sm transition-colors"
                      style={{
                        color: hospitalTypeFilter === "all" ? T.red : T.text,
                        fontWeight: hospitalTypeFilter === "all" ? 600 : 400,
                        fontFamily: sans,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      All Types
                    </button>
                    {availableTypes.map((tp) => (
                      <button
                        key={tp}
                        onClick={() => { setHospitalTypeFilter(tp); setTypeDropdownOpen(false); setHospitalDisplayLimit(50); }}
                        className="w-full text-left px-3 py-2 text-sm transition-colors"
                        style={{
                          color: hospitalTypeFilter === tp ? T.red : T.text,
                          fontWeight: hospitalTypeFilter === tp ? 600 : 400,
                          fontFamily: sans,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {tp}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Result count */}
              <span className="text-sm ml-auto" style={{ color: T.textMuted, fontFamily: sans }}>
                {showHospitals.length.toLocaleString()} of {totalRankedHospitals.toLocaleString()} hospitals
              </span>
            </div>

            {/* Hospital rows */}
            {showHospitals.slice(0, hospitalDisplayLimit).map((hospital, i) => {
              const markup = hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage;
              const isCheapestSort = hospitalSort === "cheapest";
              // When cheapest-first, invert rank: #1 = cheapest (best value)
              const displayRank = isCheapestSort && hospital.shame_rank
                ? totalRankedHospitals - hospital.shame_rank + 1
                : hospital.shame_rank;
              // Color logic: cheapest sort → low markup is green (good); expensive sort → high markup is red (bad)
              // Bands via the hospital grade scale (median-baseline metric): A/B good, F bad.
              const hospitalGrade = markup != null ? getHospitalGrade(markup) : null;
              const isGoodValue = hospitalGrade === 'A' || hospitalGrade === 'B';
              const isBadValue = hospitalGrade === 'F';
              const rankColor = isCheapestSort
                ? (isGoodValue ? T.green : isBadValue ? T.red : T.textMuted)
                : (isBadValue ? T.red : isGoodValue ? T.green : T.textMuted);
              const rankBg = isCheapestSort
                ? (isGoodValue ? "rgba(var(--civic-money-rgb),0.10)" : isBadValue ? "rgba(181,0,0,0.08)" : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)")
                : (isBadValue ? "rgba(181,0,0,0.10)" : isGoodValue ? "rgba(var(--civic-money-rgb),0.08)" : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)");
              const markupColor = isBadValue ? T.red : isGoodValue ? T.green : "#b8860b";
              // Border accent: green for cheapest sort with good value, red for expensive sort with bad value
              const borderAccent = isCheapestSort && isGoodValue ? T.green : !isCheapestSort && isBadValue ? T.red : undefined;

              const isExpanded = !collapsedHospitals.has(hospital.id);
              const toggleCollapsed = () => setCollapsedHospitals(prev => {
                const next = new Set(prev);
                if (next.has(hospital.id)) next.delete(hospital.id);
                else next.add(hospital.id);
                return next;
              });
              return (
                <div
                  key={hospital.id}
                  style={{
                    borderBottom: `1px solid ${T.border}`,
                    borderLeft: borderAccent ? `3px solid ${borderAccent}` : undefined,
                    paddingLeft: borderAccent ? 12 : 0,
                    ...(i === 0 ? { borderTop: `1px solid ${T.border}` } : {}),
                  }}
                >
                  <div
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer transition-colors"
                    style={{ padding: "clamp(14px, 2vw, 24px) 0" }}
                    onClick={toggleCollapsed}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        {displayRank != null && (
                          <span
                            className="text-xs font-medium px-2 py-1 rounded flex-shrink-0"
                            style={{
                              backgroundColor: rankBg,
                              color: rankColor,
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 11,
                            }}
                          >
                            #{displayRank.toLocaleString()} / {totalRankedHospitals.toLocaleString()}
                          </span>
                        )}
                        <h3 className="truncate" style={{ fontFamily: serif, fontSize: "clamp(17px, 2.5vw, 24px)", fontWeight: 400, color: T.text }}>
                          {titleCaseHospital(hospital.name)}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1 text-sm" style={{ color: T.textMuted }}>
                          <MapPin className="w-3.5 h-3.5" />
                          {titleCaseCity(hospital.city)}, {hospital.state}
                        </span>
                        {hospital.total_procedures != null && hospital.total_procedures > 0 && (
                          <span className="text-sm" style={{ color: T.textMuted }}>
                            {hospital.total_procedures.toLocaleString()} procedures
                          </span>
                        )}
                        {hospital.outlier_expensive_count != null && hospital.outlier_expensive_count > 0 && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: T.red }}>
                            <AlertTriangle className="w-3 h-3" />
                            {hospital.outlier_expensive_count.toLocaleString()} outliers
                          </span>
                        )}
                        {/* Deal mode toggle */}
                        {(() => {
                          const currentMode = hospitalDealMode[hospital.id] || (hospitalSort === 'cheapest' ? 'deal' : 'expensive');
                          return (
                            <span className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => setHospitalDealMode(prev => ({ ...prev, [hospital.id]: 'deal' }))}
                                className="text-xs px-2 py-0.5 min-h-[32px] rounded-l-md transition-colors"
                                style={{
                                  backgroundColor: currentMode === 'deal' ? '#16a34a' : 'transparent',
                                  color: currentMode === 'deal' ? '#fff' : T.textMuted,
                                  border: `1px solid ${currentMode === 'deal' ? '#16a34a' : T.border}`,
                                  borderRight: 'none',
                                  fontWeight: currentMode === 'deal' ? 600 : 400,
                                }}
                              >
                                Best Deals
                              </button>
                              <button
                                onClick={() => setHospitalDealMode(prev => ({ ...prev, [hospital.id]: 'expensive' }))}
                                className="text-xs px-2 py-0.5 min-h-[32px] rounded-r-md transition-colors"
                                style={{
                                  backgroundColor: currentMode === 'expensive' ? '#dc2626' : 'transparent',
                                  color: currentMode === 'expensive' ? '#fff' : T.textMuted,
                                  border: `1px solid ${currentMode === 'expensive' ? '#dc2626' : T.border}`,
                                  fontWeight: currentMode === 'expensive' ? 600 : 400,
                                }}
                              >
                                Most Expensive
                              </button>
                            </span>
                          );
                        })()}
                        <Link
                          to={`/hospital/${hospital.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs flex items-center gap-1"
                          style={{ color: T.blue, textDecoration: 'none' }}
                        >
                          View Profile <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:text-right flex-shrink-0">
                      {markup != null && (
                        <div className="text-right">
                          <p style={{
                            fontFamily: serif,
                            fontSize: "clamp(22px, 3vw, 34px)",
                            color: markupColor,
                            fontWeight: 400,
                            lineHeight: 1,
                          }}>
                            {markup >= 0 ? "+" : ""}{markup.toFixed(0)}%
                          </p>
                          <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                            vs national
                          </p>
                        </div>
                      )}
                      <ChevronDown
                        className="w-5 h-5 transition-transform"
                        style={{
                          color: T.textMuted,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ borderTop: `1px dashed ${T.border}`, padding: '8px 0 20px' }}>
                      <HospitalScenarioDeals
                        hospitalId={hospital.id}
                        theme={T as CardTheme}
                        mode={hospitalDealMode[hospital.id] || (hospitalSort === 'cheapest' ? 'deal' : 'expensive')}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Show More button */}
            {showHospitals.length > hospitalDisplayLimit && (
              <div className="text-center py-6">
                <button
                  onClick={() => setHospitalDisplayLimit((prev) => prev + 50)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors"
                  style={{
                    border: `1px solid ${T.buttonBorder}`,
                    color: T.text,
                    fontFamily: sans,
                    fontSize: 14,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  Show More ({(showHospitals.length - hospitalDisplayLimit).toLocaleString()} remaining)
                </button>
              </div>
            )}

            {/* Empty state for filter */}
            {showHospitals.length === 0 && rawHospitals.length > 0 && (
              <div className="text-center py-12">
                <p style={{ color: T.textMuted, fontSize: 15 }}>
                  No hospitals found in {hospitalStateFilter}.{" "}
                  <button onClick={() => setHospitalStateFilter("all")} style={{ color: T.red, textDecoration: "underline" }}>
                    Clear filter
                  </button>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── EMPTY STATE ── */}
        {!isLoading &&
          showScenarios.length === 0 &&
          showHospitals.length === 0 &&
          procedureResults.length === 0 &&
          codeResults.length === 0 && (
          <div className="text-center py-20">
            <Search className="w-12 h-12 mx-auto mb-4" style={{ color: T.border }} />
            <h3 style={{ fontFamily: serif, fontSize: 24, color: T.text, marginBottom: 8 }}>
              {hasQuery ? t('common:errors.noResults')
                : isCodeCategory ? t('search:codeEmpty.title')
                : t('search:placeholder')}
            </h3>
            <p style={{ color: T.textMuted, fontSize: 16, maxWidth: 400, margin: "0 auto" }}>
              {hasQuery
                ? t('search:empty.noResults', { query }) + ' ' + t('search:empty.tryDifferent')
                : isCodeCategory ? t('search:codeEmpty.body')
                : searchCategory === "scenario" ? t('search:empty.searchScenario')
                : searchCategory === "procedure" ? t('search:empty.searchProcedure')
                : t('search:empty.searchProcedure')}
            </p>
            {!hasQuery && isCodeCategory && (
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {(CODE_EXAMPLES[searchCategory] || []).map((ex) => (
                  <button
                    key={ex.code}
                    type="button"
                    onClick={() => setQuery(ex.code)}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full transition-colors"
                    style={{
                      border: `1px solid ${T.border}`,
                      color: T.text,
                      fontSize: 14,
                      fontFamily: sans,
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.hoverBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: T.blue }}>
                      {ex.code}
                    </span>
                    {ex.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* View more CTA for hospitals */}
        {!hasQuery && searchCategory !== "scenario" && !isLoading && showHospitals.length > 0 && (
          <div className="text-center mt-12">
            <Link
              to="/hospital-rankings"
              className="inline-flex items-center gap-2 px-6 py-3 transition-colors"
              style={{
                border: `1px solid ${T.buttonBorder}`,
                borderRadius: 8,
                fontSize: 15,
                color: T.text,
                fontFamily: sans,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(51,51,52,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              {t('common:actions.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </section>

      {/* ─── FOOTER TAGLINE ─── */}
      <footer className="text-center mt-20 pb-8" style={{ color: T.textMuted, fontSize: 14 }}>
        <div style={{ color: T.gold, fontSize: 32, marginBottom: 8 }}>&#9734;</div>
        <p style={{ fontFamily: serif, fontStyle: "italic", fontSize: 16 }}>
          American Hospital Prices
        </p>
        <p className="mt-2" style={{ fontSize: 13 }}>
          Transparency for the American people
        </p>
      </footer>
      </div>
    </div>
  );
};
